import { useEffect, useState } from 'react';
import { api } from '../../api.js';
import Alert from '../../components/ui/Alert.jsx';
import { EmptyState, Pill, Section } from '../../components/ui/index.jsx';
import ProviderLogo from '../../components/ui/ProviderLogo.jsx';

// Facts whose value is a provider name: shown with its badge
const BRANDED = new Set(['mail_provider', 'sending_service', 'dns_provider', 'hosting_provider', 'registrar', 'cert_issuer']);

// One line on what each kind of fact is, for people who don't speak DNS
const FACT_HELP = {
  mail_provider: 'Who runs your mailboxes.',
  mx: 'Where email sent to you is delivered.',
  sending_service: 'Services your settings allow to send email as you.',
  spf_all: 'What happens to email from servers not on your list.',
  dmarc_policy: 'What other email systems do with fake email in your name.',
  dkim_selector: 'Keys that let receivers check your email’s digital signature.',
  hosting_provider: 'The company your website runs on.',
  asn: 'The network the website’s server is on.',
  a: 'The internet address your website points to.',
  aaaa: 'The newer-style internet address your website points to.',
  cert_issuer: 'Who issued the padlock certificate for your website.',
  registrar: 'Where the domain is registered (who you pay for it).',
  expires_on: 'When the domain must be renewed. If it lapses, anyone could buy it.',
  dns_provider: 'Who holds your domain’s settings — where records are added.',
  nameserver: 'The servers that hold your domain’s settings.',
};
import { fmtDateTime } from '../../utils/format.js';

/** "Yes, ours" / "Not ours" on a discovered service. Saves straight away. */
function ConfirmButtons({ value, base, onChange, onError }) {
  const [busy, setBusy] = useState(false);
  const set = async (confirmed) => {
    setBusy(true);
    try { const res = await api(`${base}/facts/${value.id}`, { method: 'PATCH', body: { confirmed } }); onChange(res.discovery); }
    catch (err) { onError(err.message); }
    finally { setBusy(false); }
  };
  if (value.confirmed === true) {
    return <span className="fact-confirm"><Pill tone="info">You use this</Pill><button type="button" className="link-button small" disabled={busy} onClick={() => set(null)}>Undo</button></span>;
  }
  if (value.confirmed === false) {
    return <span className="fact-confirm"><Pill tone="bad">Not yours</Pill><button type="button" className="link-button small" disabled={busy} onClick={() => set(null)}>Undo</button></span>;
  }
  return (
    <span className="fact-confirm">
      <button type="button" className="btn btn-small btn-secondary" disabled={busy} onClick={() => set(true)}>Yes, we use it</button>
      <button type="button" className="btn btn-small btn-danger-ghost" disabled={busy} onClick={() => set(false)}>Not ours</button>
    </span>
  );
}

/**
 * What the public internet says about a domain: warnings, recent changes, then email /
 * website / registration sections. `base` is the domain's API path.
 */
export default function DiscoverySections({ discovery: initial, base, canManage }) {
  const [discovery, setDiscovery] = useState(initial);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState('');
  const pending = discovery.status === 'pending';

  const lookAgain = async () => {
    setError('');
    try {
      const res = await api(`${base}/discover`, { method: 'POST' });
      setDiscovery(res.discovery);
      setQueued(true);
    } catch (err) { setError(err.message); }
  };

  // While a look-up is running, fetch the results by ourselves (no "refresh the page")
  const waiting = pending || queued;
  const startedAt = discovery.last_run?.started_at;
  useEffect(() => {
    if (!waiting) return undefined;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const res = await api(base);
        const next = res.discovery;
        if (next.status !== 'pending' && next.last_run?.started_at !== startedAt) { setDiscovery(next); setQueued(false); }
      } catch { /* try again next tick */ }
      if (tries > 30) { clearInterval(t); setQueued(false); }
    }, 4000);
    return () => clearInterval(t);
  }, [waiting, base, startedAt]);

  const lastRun = discovery.last_run;
  return (
    <>
      <Section
        title="What we found"
        description="Looked up from public records (DNS, the domain registry, your website’s certificate). You don’t have to set any of this up for us — but it tells us what genuine email and websites from you look like."
        actions={<button type="button" className="btn btn-secondary btn-small" onClick={lookAgain} disabled={waiting}>{waiting ? 'Looking…' : 'Look again'}</button>}
      >
        {error && <Alert tone="danger">{error}</Alert>}
        {pending && <EmptyState>We&rsquo;re looking this domain up. It usually takes under a minute — the results will appear here.</EmptyState>}
        {lastRun && (
          <p className="muted small">
            Last looked {fmtDateTime(lastRun.started_at)}.{' '}
            {lastRun.status === 'partial' && 'Some lookups didn’t answer; we kept what we knew from before. '}
            {lastRun.status === 'failed' && 'The lookups didn’t answer this time; we’ll try again. '}
            We check again every day.
          </p>
        )}
        {queued && !pending && <Alert tone="info">Looking again now. What changed will appear here in a minute or so.</Alert>}
        {discovery.warnings.map((w) => (
          <Alert key={w.key} tone={w.level === 'bad' ? 'danger' : 'warn'}>
            {w.text}{w.help && <> <a href={w.help} target="_blank" rel="noreferrer">How to change DNS records with {discovery.dns_provider}</a>.</>}
          </Alert>
        ))}
      </Section>

      {discovery.changes.length > 0 && (
        <Section title="Changes in the last 30 days" description="If you didn’t expect one of these, someone may have changed your domain’s settings." flush>
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>What</th><th>Change</th><th>Value</th><th>When</th></tr></thead>
              <tbody>
                {discovery.changes.map((c) => (
                  <tr key={`${c.id}-${c.change}`}>
                    <td data-label="What">{c.label}</td>
                    <td data-label="Change"><Pill tone={c.change === 'added' ? 'info' : 'warn'}>{c.change === 'added' ? 'New' : 'Gone'}</Pill></td>
                    <td data-label="Value" className="mono">{c.value}</td>
                    <td data-label="When" className="small">{fmtDateTime(c.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {discovery.sections.map((s) => (
        <Section key={s.key} title={s.title} flush>
          <div className="table-scroll">
            <table className="table table-stack fact-table">
              <tbody>
                {s.rows.map((r) => (
                  <tr key={r.type}>
                    <th scope="row">{r.label}{FACT_HELP[r.type] && <span className="fact-help">{FACT_HELP[r.type]}</span>}</th>
                    <td data-label={r.label}>
                      <ul className="fact-values">
                        {r.values.map((v) => (
                          <li key={v.id}>
                            {BRANDED.has(r.type)
                              ? <span className="provider-name"><ProviderLogo name={v.value} />{v.value}</span>
                              : <span className={r.type === 'mx' || r.type === 'a' || r.type === 'aaaa' || r.type === 'asn' ? 'mono' : undefined}>{v.value}</span>}
                            {v.confirmable && canManage && <ConfirmButtons value={v} base={base} onChange={setDiscovery} onError={setError} />}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ))}
    </>
  );
}
